import {
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawRenderMode,
  TerraDrawSelectMode,
  type GeoJSONStoreFeatures,
} from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";
import { v4 as uuidv4 } from "uuid";
import {
  CIRCLE_DRAW_MODE,
  POLYGON_DRAW_MODE,
  READONLY_DRAW_MODE,
  RECTANGLE_DRAW_MODE,
  SELECT_DRAW_MODE,
  type DrawMode,
} from "./draw-modes";

const EDITABLE_POLYGON_FLAGS = {
  feature: {
    draggable: true,
    coordinates: {
      midpoints: { draggable: true },
      draggable: true,
      deletable: true,
    },
  },
};

const EDITABLE_CIRCLE_FLAGS = {
  feature: {
    draggable: true,
    coordinates: { draggable: true },
  },
};

const POINTER_DISTANCE = 15;

const INACTIVE_COLOR = "#3bb2d0";
const ACTIVE_COLOR = "#fbb03b";

export type DrawEvent = "create" | "update" | "selectionchange" | "modechange";

type Listener = (payload: any) => void;

const TERRA_DRAW_MAX_PRECISION = 9;

function sanitizePosition(position: GeoJSON.Position): GeoJSON.Position {
  const round = (n: number) =>
    Math.round(n * 10 ** TERRA_DRAW_MAX_PRECISION) /
    10 ** TERRA_DRAW_MAX_PRECISION;
  return [round(position[0]), round(position[1])];
}


function toDrawablePolygon(coordinates: GeoJSON.Position[][]): GeoJSON.Polygon {
  return {
    type: "Polygon",
    coordinates: [coordinates[0].map(sanitizePosition)],
  };
}

function toDrawableGeometries(
  feature: GeoJSON.Feature
): GeoJSON.Polygon[] | GeoJSON.LineString[] | GeoJSON.Point[] | [] {
  const geometry = feature.geometry;
  switch (geometry.type) {
    case "Polygon":
      return [toDrawablePolygon(geometry.coordinates)];
    case "MultiPolygon":
      return geometry.coordinates.map(toDrawablePolygon);
    default:
      return [];
  }
}

const EDITABLE_DRAW_MODES: DrawMode[] = [
  POLYGON_DRAW_MODE,
  RECTANGLE_DRAW_MODE,
  CIRCLE_DRAW_MODE,
];

function drawModeFor(feature: GeoJSON.Feature): DrawMode {
  const mode = feature.properties?.mode;
  return EDITABLE_DRAW_MODES.includes(mode) ? mode : POLYGON_DRAW_MODE;
}

function polygonStyles(color: string) {
  return {
    fillColor: color as `#${string}`,
    fillOpacity: 0.1,
    outlineColor: color as `#${string}`,
    outlineWidth: 2,
  };
}


export class BoundaryDraw {
  private terraDraw: TerraDraw | null = null;
  private map: any = null;
  private mode: DrawMode = SELECT_DRAW_MODE;
  private listeners = new Map<DrawEvent, Set<Listener>>();
  private drawIds = new Map<string, string[]>();
  private pending: GeoJSON.Feature[] = [];
  private pendingRestore: GeoJSONStoreFeatures[] = [];
  private applying = false;
  private detachStyleListener: (() => void) | null = null;
  private geometries = new Map<string, string>();
  private selectedDrawId: string | null = null;

  attach(mapRefOrMap: any) {
    const map =
      typeof mapRefOrMap?.getMap === "function"
        ? mapRefOrMap.getMap()
        : mapRefOrMap;
    if (!map || this.map === map) return;
    if (this.map) this.detach();

    this.map = map;
    this.terraDraw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      idStrategy: {
        isValidId: (id) => typeof id === "string" && id.length > 0,
        getId: () => uuidv4(),
      },
      modes: [
        new TerraDrawSelectMode({
          pointerDistance: POINTER_DISTANCE,
          flags: {
            [POLYGON_DRAW_MODE]: EDITABLE_POLYGON_FLAGS,
            [RECTANGLE_DRAW_MODE]: EDITABLE_POLYGON_FLAGS,
            [CIRCLE_DRAW_MODE]: EDITABLE_CIRCLE_FLAGS,
          },
          styles: {
            selectedPolygonColor: ACTIVE_COLOR,
            selectedPolygonFillOpacity: 0.1,
            selectedPolygonOutlineColor: ACTIVE_COLOR,
            selectedPolygonOutlineWidth: 2,
            midPointColor: ACTIVE_COLOR,
            selectionPointColor: ACTIVE_COLOR,
          },
        }),
        new TerraDrawPolygonMode({ styles: polygonStyles(INACTIVE_COLOR) }),
        new TerraDrawRectangleMode({ styles: polygonStyles(INACTIVE_COLOR) }),
        new TerraDrawCircleMode({ styles: polygonStyles(INACTIVE_COLOR) }),
        new TerraDrawRenderMode({
          modeName: READONLY_DRAW_MODE,
          styles: {
            polygonFillColor: INACTIVE_COLOR,
            polygonFillOpacity: 0.1,
            polygonOutlineColor: INACTIVE_COLOR,
            polygonOutlineWidth: 2,
          },
        }),
      ],
    });

    this.terraDraw.on("finish", (id, context) => {
      if (context.action !== "draw") return;
      const feature = this.readFeature(id);
      if (!feature) return;
      this.drawIds.set(String(id), [String(id)]);
      this.geometryChanged(feature);
      this.emit("create", { features: [feature] });
      setTimeout(() => this.changeMode(SELECT_DRAW_MODE), 0);
    });

    this.terraDraw.on("change", (ids, type) => {
      if (type !== "update" || this.applying) return;
      const features = ids
        .filter((id) => this.isTracked(id))
        .map((id) => this.readFeature(id))
        .filter((feature): feature is GeoJSON.Feature =>
          Boolean(feature) && this.geometryChanged(feature!)
        );
      if (features.length) this.emit("update", { features });
    });

    this.terraDraw.on("select", (id) => {
      this.selectedDrawId = String(id);
      const feature = this.readFeature(id);
      this.emit("selectionchange", { features: feature ? [feature] : [] });
    });

    this.terraDraw.on("deselect", () => {
      this.selectedDrawId = null;
      this.emit("selectionchange", { features: [] });
    });

    this.startDraw();

    const onStyleData = () => this.redraw();
    map.on("styledata", onStyleData);
    this.detachStyleListener = () => map.off("styledata", onStyleData);
  }


  private startDraw() {
    if (!this.terraDraw || !this.map) return false;
    if (this.terraDraw.enabled) return true;
    const map = this.map;

    if (!map.isStyleLoaded()) {
      map.once("idle", () => this.startDraw());
      return false;
    }

    const original = map.isStyleLoaded;
    map.isStyleLoaded = () => true;
    try {
      this.terraDraw.start();
      this.terraDraw.setMode(this.mode);
    } catch (err) {
      console.error("[boundary-draw] failed to start Terra Draw:", err);
      this.stopDraw();
      return false;
    } finally {
      map.isStyleLoaded = original;
    }

    const buffered = this.pending;
    this.pending = [];
    buffered.forEach((feature) => this.add(feature));

    if (this.pendingRestore.length) {
      const restore = this.pendingRestore;
      this.pendingRestore = [];
      this.addDrawFeatures(restore);
    }
    return true;
  }

  detach() {
    this.detachStyleListener?.();
    this.detachStyleListener = null;
    this.stopDraw();
    this.terraDraw = null;
    this.map = null;
    this.drawIds.clear();
  }

  add(feature: GeoJSON.Feature) {
    if (!this.terraDraw) {
      this.pending.push(feature);
      return;
    }

    const appId = String(feature.id ?? uuidv4());
    const geometries = toDrawableGeometries(feature);
    if (!geometries.length) {
      console.warn(
        `[boundary-draw] feature ${appId} has unsupported geometry ` +
          `"${feature.geometry.type}" and is not shown on the map`
      );
      return;
    }

    if (this.drawIds.has(appId)) this.delete([appId]);

    const mode =
      geometries.length > 1 ? READONLY_DRAW_MODE : drawModeFor(feature);

    const drawFeatures = geometries.map((geometry, i) => ({
      id: geometries.length > 1 ? `${appId}:${i}` : appId,
      type: "Feature" as const,
      geometry,
      properties: { ...feature.properties, mode },
    }));

    this.applying = true;
    let acceptedIds: string[];
    try {
      acceptedIds = this.addDrawFeatures(
        drawFeatures as GeoJSONStoreFeatures[]
      );
    } finally {
      this.applying = false;
    }
    if (!acceptedIds.length) return;

    this.drawIds.set(appId, acceptedIds);
    this.geometries.set(appId, JSON.stringify(feature.geometry));
  }

  delete(ids: string[]) {
    if (!this.terraDraw) {
      this.pending = this.pending.filter((f) => !ids.includes(String(f.id)));
      return;
    }
    const drawIds = ids.flatMap((id) => this.drawIds.get(id) ?? []);
    if (this.selectedDrawId && drawIds.includes(this.selectedDrawId)) {
      this.selectedDrawId = null;
    }
    if (drawIds.length) this.terraDraw.removeFeatures(drawIds);
    ids.forEach((id) => {
      this.drawIds.delete(id);
      this.geometries.delete(id);
    });
  }

  deleteAll() {
    this.pending = [];
    this.drawIds.clear();
    this.geometries.clear();
    this.selectedDrawId = null;
    try {
      this.terraDraw?.clear();
    } catch {
      /* sources already removed */
    }
  }

  changeMode(mode: DrawMode) {
    this.mode = mode;
    this.terraDraw?.setMode(mode);
    this.emit("modechange", { mode });
  }

  getMode(): DrawMode {
    return this.mode;
  }

  select(appId: string): boolean {
    if (!this.terraDraw) return false;
    const drawIds = this.drawIds.get(appId);

    if (this.selectedDrawId) {
      try {
        this.terraDraw.deselectFeature(this.selectedDrawId);
      } catch {
        /* already gone */
      }
      this.selectedDrawId = null;
    }

    if (!drawIds || drawIds.length !== 1) return false;
    this.changeMode(SELECT_DRAW_MODE);
    try {
      this.terraDraw.selectFeature(drawIds[0]);
      this.selectedDrawId = drawIds[0];
    } catch (err) {
      console.warn("[boundary-draw] could not select feature:", err);
      return false;
    }
    return true;
  }

  on(event: DrawEvent, listener: Listener) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(listener);
    return () => this.off(event, listener);
  }

  off(event: DrawEvent, listener: Listener) {
    this.listeners.get(event)?.delete(listener);
  }

  private addDrawFeatures(features: GeoJSONStoreFeatures[]): string[] {
    const validations = this.terraDraw?.addFeatures(features) ?? [];
    const accepted: string[] = [];
    validations.forEach((validation) => {
      if (validation.valid) {
        accepted.push(String(validation.id));
        return;
      }
      console.error(
        `[boundary-draw] Terra Draw rejected feature ${String(validation.id)}: ` +
          (validation.reason ?? "no reason given")
      );
    });
    return accepted;
  }

  private stopDraw() {
    try {
      this.terraDraw?.stop();
    } catch {
      /* sources already removed */
    }
  }

  private redraw() {
    if (!this.terraDraw || !this.map) return;

    if (!this.terraDraw.enabled) {
      this.startDraw();
      return;
    }

    try {
      if (this.map.getSource("td-polygon")) return;
    } catch {
      return;
    }

    const trackedIds = new Set([...this.drawIds.values()].flat());
    this.pendingRestore = this.terraDraw
      .getSnapshot()
      .filter((feature) => trackedIds.has(String(feature.id)));

    this.stopDraw();
    this.startDraw();
  }

  private readFeature(id: string | number): GeoJSON.Feature | null {
    const feature = this.terraDraw?.getSnapshotFeature(id);
    if (!feature) return null;
    return { ...feature, id: this.appIdFor(id) } as GeoJSON.Feature;
  }

  private geometryChanged(feature: GeoJSON.Feature): boolean {
    const id = String(feature.id);
    const geometry = JSON.stringify(feature.geometry);
    if (this.geometries.get(id) === geometry) return false;
    this.geometries.set(id, geometry);
    return true;
  }

  private isTracked(drawId: string | number): boolean {
    for (const ids of this.drawIds.values()) {
      if (ids.includes(String(drawId))) return ids.length === 1;
    }
    return false;
  }

  private appIdFor(drawId: string | number): string {
    for (const [appId, ids] of this.drawIds) {
      if (ids.includes(String(drawId))) return appId;
    }
    return String(drawId);
  }

  private emit(event: DrawEvent, payload: any) {
    this.listeners.get(event)?.forEach((listener) => listener(payload));
  }
}
